import { ApiTenderFree, FreeGeneralCat, TenderFreeInfo } from './types';
import { IDocsCat, TenderCatName, TenderSubCatName } from '../Tender/types';
import { toDocsSubCats, toGenericCat, toGenericSubCat } from '../Tender/helpers';

export default class TenderFree {
  public readonly id: string;
  public readonly info: TenderFreeInfo;
  public readonly generalCat: FreeGeneralCat;
  public readonly docsCat: IDocsCat;

  constructor(private readonly raw: ApiTenderFree) {
    this.id = raw.id ?? '';
    this.info = this.toInfo();
    this.generalCat = this.toGeneralCat();
    this.docsCat = this.toDocsCat();
  }

  private toInfo(): TenderFreeInfo {
    return {
      name: this.raw.tender?.title ?? undefined,
      deadline: this.raw.tender?.tenderPeriod?.endDate ?? undefined,
      published: this.raw.date ?? undefined,
      buyerName: this.raw.parties?.find(party => !!party.name)?.name ?? undefined,
      buyerLink: this.raw.parties?.find(party => !!party.details?.url)?.details?.url ?? undefined,
      estValue: {
        amount: this.raw.tender?.value?.amount ?? null,
        currency: this.raw.tender?.value?.currency ?? null
      }
    };
  }

  private toGeneralCat(): FreeGeneralCat {
    const generalCat = toGenericCat(TenderCatName.Overview);

    return {
      ...generalCat,
      [TenderSubCatName.ShortDescription]: {
        ...toGenericSubCat(TenderSubCatName.ShortDescription),
        description: this.raw.tender?.description ?? undefined
      },
      [TenderSubCatName.Location]: {
        ...toGenericSubCat(TenderSubCatName.Location),
        placeOfPerformance: this.raw.tender?.items?.find(loc => loc.performanceNuts)?.performanceNuts ?? undefined,
        location:
          this.raw.tender?.items?.find(loc => loc.deliveryLocation?.description)?.deliveryLocation?.description ??
          undefined
      },
      [TenderSubCatName.Procurement]: {
        ...toGenericSubCat(TenderSubCatName.Procurement),
        typeOfProcedure: this.raw.tender?.procurementMethodDetails ?? undefined,
        linkForSubmittingTender: this.raw.tender?.submissionUrl ?? undefined,
        frameworkAgreement: this.raw.tender?.techniques?.hasFrameworkAgreement ?? undefined,
        deadline: this.raw.tender?.tenderPeriod?.endDate ?? undefined,
        published: this.raw.date ?? undefined,
        referenceNumber: this.raw.tender?.id ?? undefined,
        deadlineOfQuestions: this.raw.tender?.enquiryPeriod?.endDate ?? undefined,
        awardCriteria: this.raw.tender?.awardCriteriaDetails ?? undefined
      },
      [TenderSubCatName.Contact]: {
        ...toGenericSubCat(TenderSubCatName.Contact),
        buyerName: this.raw.parties?.find(party => !!party?.name)?.name ?? undefined,
        buyerId: this.raw.parties?.find(party => !!party?.identifier?.id)?.identifier?.id ?? undefined,
        phone: this.raw.parties?.find(party => party?.contactPoint?.telephone)?.contactPoint?.telephone ?? undefined,
        contactPerson: this.raw.parties?.find(party => party?.contactPoint?.name)?.contactPoint?.name ?? undefined,
        email: this.raw.parties?.find(party => party.contactPoint?.email)?.contactPoint?.email ?? undefined,
        city: this.raw.parties?.find(party => party?.address?.locality)?.address?.locality ?? undefined,
        postalAddress:
          this.raw.parties?.find(party => party?.address?.streetAddress)?.address?.streetAddress ?? undefined,
        buyerNutsCode: this.raw.parties?.find(party => party?.address?.nuts)?.address?.nuts ?? undefined,
        postcode: this.raw.parties?.find(party => party?.address?.postalCode)?.address?.postalCode ?? undefined
      },
      [TenderSubCatName.Contract]: {
        ...toGenericSubCat(TenderSubCatName.Contract),
        estValue: {
          amount: this.raw.tender?.value?.amount ?? null,
          currency: this.raw.tender?.value?.currency ?? null
        },
        contractDuration: this.raw.tender?.contractPeriod?.durationInDays ?? undefined,
        contractRenewal: this.raw.tender?.renewal?.description ?? undefined,
        contractStart: this.raw.tender?.contractPeriod?.startDate ?? undefined,
        contractEnd: this.raw.tender?.contractPeriod?.endDate ?? undefined
      }
    };
  }
  private toDocsCat(): IDocsCat {
    return {
      ...toGenericCat(TenderCatName.Docs),
      ...toDocsSubCats(this.raw.documents ?? []),
      linkToDocsSource: this.raw.tender?.documentUrl ?? undefined
    };
  }
}
